import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { CgProfile } from "react-icons/cg";
import { FaBullhorn } from "react-icons/fa";
import { GiTrade } from "react-icons/gi";
import { IoExpandOutline } from "react-icons/io5";
import { LiaCubesSolid } from "react-icons/lia";
import { TbReport } from "react-icons/tb";
import { VscPreview } from "react-icons/vsc";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import useFetch from "../../../hooks/useFetchMSAL";
import DropdownItem from "../../../models/DropdownItem.model";
import TabDetail from "../../../models/TabDetail.model";
import { ANNUAL_SUMMARY_TAB_ID, FORECAST_REVIEW_TAB_ID,
     LONG_TERM_PROMOTION_SUB_TAB_ID, PROMOTION_PLANNING_TAB_ID,
      PROMO_MASTER_TAB_ID, SHORT_TERM_PROMOTION_SUB_TAB_ID,
       TOAST_CONTAINER_ID, TRADE_SPEND_REPORT_TAB_ID,
        VOLUME_FORECAST_TAB_ID } from "../../../shared/constants";
import { getToastOptions } from "../../../shared/functions";
import { RootState } from "../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../store/slices/trade-and-promo-planning-tab-slice";
import RadioDropdown from "../../UI/RadioDropdown/RadioDropdown";
import Tabs from "../../UI/Tabs/Tabs";
import AnnualSummaryTab from "./AnnualSummaryTab/AnnualSummaryTab";
import ForecastReviewTab from "./ForecastReview/ForecastReviewTab";
import PromoMasterTab from "./PromoMasterTab/PromoMasterTab";
import PromotionPlanningTab from "./PromotionPlanningTab/PromotionPlanningTab";
import TradeSpendReportTab from "./TradeSpendReport/TradeSpendReportTab";
import VolumeForecastTab from "./VolumeForecastTab/VolumeForecastTab";

const TradeAndPromoPlanningTab = () => {
    // States
    const [customerDropdownData, setCustomerDropdownData] = useState<DropdownItem[]>([]);

    // Variables
    const [, fetchData] = useFetch([]);
    const subTabs: TabDetail[] = [
        {
            id: VOLUME_FORECAST_TAB_ID,
            name: "Volume Forecast",
            icon: <LiaCubesSolid />,
            content: <VolumeForecastTab />
        },
        {
            id: PROMOTION_PLANNING_TAB_ID,
            name: "Promotion Planning",
            icon: <IoExpandOutline />,
            content: <PromotionPlanningTab />
        },
        {
            id: PROMO_MASTER_TAB_ID,
            name: "Promotion Master",
            icon: <FaBullhorn />,
            content: <PromoMasterTab />
        },
        {
            id: FORECAST_REVIEW_TAB_ID,
            name: "Forecast Review",
            icon: <VscPreview />,
            content: <ForecastReviewTab />
        },
        {
            id: ANNUAL_SUMMARY_TAB_ID,
            name: "Annual Summary",
            icon: <TbReport />,
            content: <AnnualSummaryTab />
        },
        {
            id: TRADE_SPEND_REPORT_TAB_ID,
            name: "Trade Spend Report",
            icon: <GiTrade />,
            content: <TradeSpendReportTab />
        }
    ];

    // Redux variables
    const dispatchFn = useDispatch();
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    
    /**
     * Handler to set active tab ID
     * @param {string} activeSubTabId Tab ID to be set as active
     */
    const setActiveSubTabId = (activeSubTabId: string): void => {
        dispatchFn(tradeAndPromoPlanningTabActions.setActiveSubTabId(activeSubTabId));
    };

    /**
     * Handler for change in Customer dropdown
     * @param {string} customerId Selected customer ID.
     */
    const customerDropdownChangeHandler = (customerId: string): void => {
        const customer: DropdownItem | undefined = customerDropdownData.find(c => parseInt(c.value) === parseInt(customerId));

        if (customer) {
            dispatchFn(tradeAndPromoPlanningTabActions.setGlobalCustomer({
                id: parseInt(customer.value),
                name: customer.name
            }));
            dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                [VOLUME_FORECAST_TAB_ID]: false,
                [PROMOTION_PLANNING_TAB_ID]: false,
                [LONG_TERM_PROMOTION_SUB_TAB_ID]: false,
                [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
                [FORECAST_REVIEW_TAB_ID]: false,
                [ANNUAL_SUMMARY_TAB_ID]: false,
                [TRADE_SPEND_REPORT_TAB_ID]: false
            }));
        }
    };

    /**
     * Async function to fetch customer dropdown data from GET /customer-dropdowns
     */
    const fetchCustomerDropdownData = useCallback(async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching customer details...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<{
                data: DropdownItem[] | null;
                error: string | null;
            }> = await fetchData("/customer-dropdowns", {
                method: "GET"
            });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (fetchAPIResponse.data.data) {
                setCustomerDropdownData(fetchAPIResponse.data.data);

            } else {
                toast.error('Error in fetching the customer details', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching the customer details'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [fetchData]);

    // To fetch dropdown data of all customers
    useEffect(() => {
        fetchCustomerDropdownData();
    }, [fetchCustomerDropdownData]);

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col col-12 mb-2 text-center">
                        <RadioDropdown
                            id="global-customer-dropdown"
                            icon={<CgProfile size={20} />}
                            label="Global Customer"
                            selectedValue={globalCustomer.id.toString()}
                            items={customerDropdownData}
                            onItemChanged={(value: any) => customerDropdownChangeHandler(value)}
                        />
                </div>
                <div className="col col-12 mb-2">
                    <Tabs
                        tabs={subTabs}
                        activeTabIndex={subTabs.findIndex(tab => tab.id === activeSubTabId)}
                        getActiveTabId={setActiveSubTabId}
                    />
                </div>
            </div>
        </div>
    );
};

export default TradeAndPromoPlanningTab;
